// Acting on an Ask list (steps 4.11, 4.12): the shapes the ask function and crm_preview_action return, the words the
// confirm card uses, and small pure helpers. Ask proposes, a person confirms, and the normal approval gate (4.10)
// sends: nothing is ever sent from a question. Labels are copies of supabase/functions/ask/lib/action.ts.

export const ACTION_KINDS = ["email", "tag_people", "enrol_in_sequence", "create_tasks", "set_stage", "link_introducer"] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];
export type ActionParams = Record<string, unknown>;

export const ACTION_LABELS: Record<ActionKind, string> = {
  email: "Email them (a campaign)",
  tag_people: "Tag them",
  enrol_in_sequence: "Add them to a sequence",
  create_tasks: "Create a follow-up task for each",
  set_stage: "Move the firms to a stage",
  link_introducer: "Record who introduced them",
};

export const EXCLUSION_LABELS: Record<string, string> = {
  suppressed: "Suppressed or asked not to be contacted",
  unsubscribed: "Unsubscribed",
  existing_customer: "Already a customer or in a deal with you",
  in_conversation: "Already in a conversation with you",
  known_contact: "Someone you already know (your email, contacts or CRM)",
  contacted_before: "Contacted before you started again",
  firm_closed: "Their firm is closed",
  no_contact: "Firm has no contact person",
  no_email: "No email address",
  email_invalid: "Email address bounced or is invalid",
  email_not_verified: "Email address not verified",
  channel_not_allowed: "Email not allowed for them (for example a sole trader or partnership under PECR)",
  in_sequence: "Already in a sequence",
  in_other_campaign: "Already on another campaign that has not finished",
  message_pending: "Already has a message waiting to go",
  recently_contacted: "Contacted recently",
  duplicate_email: "Same email address as someone else on the list",
  no_change: "Already tagged that way",
  no_firm: "Not linked to a firm",
  already_at_stage: "Already at that stage",
  is_introducer: "Is the introducer",
  already_introduced: "Already recorded as introduced by them",
  draft_failed: "The agent couldn't write to them",
};

export function exclusionLabel(reason: string): string {
  if (EXCLUSION_LABELS[reason]) return EXCLUSION_LABELS[reason]!;
  if (reason.startsWith("draft_failed")) return "The agent couldn't write to them";
  return reason.replace(/_/g, " ");
}

export const LARGE_THRESHOLD = 50;

export interface PreviewRow {
  person_id: string | null;
  organisation_id: string | null;
  name: string | null;
  firm: string | null;
  email: string | null;
  nation?: string | null;
  reason?: string;
}

export interface ActionPreview {
  targets: "people" | "firms";
  channel: string | null;
  matched: number;
  /** How many match the filters; more than matched when the list's limit took only its first rows. */
  matched_total: number;
  limited: boolean;
  included: number;
  excluded: number;
  by_reason: Record<string, number>;
  sample: PreviewRow[];
  exclusions: PreviewRow[];
  limits: { max?: number; large_threshold?: number; needs_large_confirm?: boolean; max_per_day?: number | null; campaigns_today?: number | null; enrolled_today?: number | null; min_days_since_contact?: number | null };
  blocked: { code: string; message: string }[];
}

export interface ActionReply {
  action: ActionKind;
  label: string;
  params: ActionParams;
  name: string | null;
  brief: string | null;
  needs: string[];
  choices: { sequences?: { id: string; name: string }[]; introducers?: { id: string; name: string; firm: string | null }[] };
  preview: ActionPreview | null;
  error: string | null;
}

/** What crm_preview_action returns (the full lists); shapePreview trims it for the card. */
export interface PreviewResponse {
  targets?: string;
  channel?: string | null;
  matched?: number;
  matched_total?: number;
  limited?: boolean;
  included?: number;
  excluded?: number;
  by_reason?: Record<string, number>;
  recipients?: PreviewRow[];
  exclusions?: PreviewRow[];
  limits?: ActionPreview["limits"];
  blocked?: { code: string; message: string }[];
}

export const SAMPLE_SIZE = 10;

export function shapePreview(d: PreviewResponse): ActionPreview {
  return {
    targets: d.targets === "firms" ? "firms" : "people",
    channel: d.channel ?? null,
    matched: Number(d.matched ?? 0),
    matched_total: Number(d.matched_total ?? d.matched ?? 0),
    limited: d.limited === true,
    included: Number(d.included ?? 0),
    excluded: Number(d.excluded ?? 0),
    by_reason: d.by_reason ?? {},
    sample: (d.recipients ?? []).slice(0, SAMPLE_SIZE),
    exclusions: (d.exclusions ?? []).slice(0, 200),
    limits: d.limits ?? {},
    blocked: d.blocked ?? [],
  };
}

/** A blank proposal for the manual "Act on these" menu: the person fills in the rest. */
export function blankAction(action: ActionKind): ActionReply {
  return { action, label: ACTION_LABELS[action], params: {}, name: null, brief: null, needs: [], choices: {}, preview: null, error: null };
}

/** What still has to be chosen before a preview: the sequence, the introducer, the stage, the tag, the task title. */
export function missing(a: Pick<ActionReply, "action" | "params">): string[] {
  const p = a.params;
  switch (a.action) {
    case "enrol_in_sequence": return p.sequence ? [] : ["sequence"];
    case "link_introducer": return p.introducer ? [] : ["introducer"];
    case "set_stage": return p.stage ? [] : ["stage"];
    case "create_tasks": return typeof p.title === "string" && p.title.trim() ? [] : ["title"];
    case "tag_people": return (Array.isArray(p.add) && p.add.length) || (Array.isArray(p.remove) && p.remove.length) ? [] : ["tag"];
    default: return [];
  }
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString("en-GB")} ${n === 1 ? one : many}`;

/** "What will happen", in one sentence. */
export function whatWillHappen(a: ActionReply, names: { stage?: string; sequence?: string; introducer?: string } = {}): string {
  const n = a.preview?.included ?? 0;
  const who = a.preview?.targets === "firms" ? plural(n, "firm", "firms") : plural(n, "person", "people");
  const p = a.params;
  switch (a.action) {
    case "email":
      return `The outreach agent will write a personal email to each of ${who}. Nothing is sent until you approve them on Home.`;
    case "tag_people": {
      const add = Array.isArray(p.add) ? (p.add as string[]) : [];
      const remove = Array.isArray(p.remove) ? (p.remove as string[]) : [];
      const parts = [add.length ? `add ${add.join(", ")}` : "", remove.length ? `remove ${remove.join(", ")}` : ""].filter(Boolean);
      return `${parts.join(" and ").replace(/^./, (c) => c.toUpperCase())} for ${who}.`;
    }
    case "enrol_in_sequence":
      return `Start ${who} on ${names.sequence ? `the ${names.sequence} sequence` : "the sequence"}. Each email still waits for your approval.`;
    case "create_tasks":
      return `Create a task for you for each of ${who}: "${String(p.title ?? "")}".`;
    case "set_stage":
      return `Move ${who} to ${names.stage ?? String(p.stage ?? "the stage")}.`;
    case "link_introducer":
      return `Record that ${names.introducer ?? "they"} introduced ${who}.`;
  }
}

/** Exclusions grouped by reason, most common first. */
export function groupExclusions(rows: PreviewRow[], byReason: Record<string, number>): { reason: string; label: string; count: number; rows: PreviewRow[] }[] {
  return Object.entries(byReason)
    .map(([reason, count]) => ({ reason, label: exclusionLabel(reason), count, rows: rows.filter((r) => r.reason === reason) }))
    .sort((x, y) => y.count - x.count || x.label.localeCompare(y.label));
}

/** A campaign row from v_campaigns. */
export interface Campaign {
  id: string;
  name: string;
  brief: string;
  channel: string;
  status: "draft" | "confirmed" | "drafting" | "awaiting_approval" | "sending" | "done" | "cancelled";
  included_count: number;
  excluded_count: number;
  drafted: number;
  awaiting_approval: number;
  approved: number;
  sent: number;
  failed: number;
  rejected: number;
  cancelled: number;
  batch_id: string | null;
  created_by: string;
  created_at: string;
}

export const CAMPAIGN_STATUS: Record<Campaign["status"], string> = {
  draft: "Draft",
  confirmed: "Waiting for the agent",
  drafting: "Being written",
  awaiting_approval: "Waiting for your approval",
  sending: "Sending",
  done: "Done",
  cancelled: "Cancelled",
};

export function campaignCancellable(c: Pick<Campaign, "status">): boolean {
  return !["done", "cancelled"].includes(c.status);
}

/** The result of create_campaign. */
export interface CampaignCreated {
  campaign_id: string;
  status: string;
  included: number;
  excluded: number;
}

/** The result of run_bulk_action. */
export interface BulkResult {
  action: string;
  done: number;
  failed: number;
  excluded: number;
  results: { person_id: string | null; organisation_id: string | null; name: string | null; ok: boolean; error: string | null }[];
}
